import React from 'react';
import { Box, Typography } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { observer } from 'mobx-react-lite';
import PageInnerContainer from 'pages/components/pageInnerContainer';
import BlockHeader from 'pages/components/blockHeader';
import { useContext, useEffect, useState } from 'react';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import { formatBackToFrontDate } from 'shared/lib/utils/globalUtils';
import StaticTable from 'shared/lib/extendedComponents/tables/staticTable/staticTable';
import { map } from 'lodash-es';
import StaticTableCellItem from 'shared/lib/extendedComponents/tables/staticTable/staticTableCellItem';
import { TableLinksHelper } from 'entities/table/model/tableLinksHelper';
import StaticTableCellItemLink from 'shared/lib/extendedComponents/tables/staticTable/staticTableCellItemLink';
import UploadGrid from 'pages/uploadPage/components/uploadGrid';
import { tablesApi } from 'entities/table/api/tablesApi';
import { TableModel } from 'entities/table/model/tableModel';
import TableViewController, {
  useTableHeader,
} from 'widgets/tables/tableViewController';
import SaveTableController from 'widgets/tables/saveTableController';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import FavoritesButton from 'shared/lib/components/controls/buttons/favoritesButton';
import mobxStore from 'app/store/rootStore';
import { useTranslation } from 'react-i18next';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';

export default observer(function ExplorerTable({
  tableModel,
}: {
  tableModel: TableModel;
}) {
  const { t } = useTranslation();
  const {
    mobxStore: {
      authStore: { user },
    },
  } = useContext(MobxContext);
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const { isLessLaptop, isLessTablet } = useBooleanScreens();

  const header = useTableHeader();

  const [isEditing, setIsEditing] = useState(false);

  const [tableHelper, setTableHelper] = useState(
    () =>
      new TableLinksHelper({
        links: map(tableModel?.links, (link) => ({
          id: link.id,
          name: link.data?.name || '',
          description: link.data?.description || '',
          url: link.data?.url || '',
        })),
      })
  );

  useEffect(() => {
    setTableHelper(
      new TableLinksHelper({
        links: map(tableModel?.links, (link) => ({
          id: link.id,
          name: link.data?.name || '',
          description: link.data?.description || '',
          url: link.data?.url || '',
        })),
      })
    );
  }, [tableModel?.links, isEditing]);

  const isFavorite = tableModel.data?.is_favorite;
  const toggleFavorite = async () => {
    try {
      const newIsFavorite = !isFavorite;
      await tableModel.setFavorite(newIsFavorite);
    } catch (e) {
      console.error(e);
      mobxStore.notificationsStore.errorMessage = t(
        'notifications.errors.default'
      );
    }
  };

  const tableData = tableModel.data;

  return tableData ? (
    <PageInnerContainer>
      <BlockHeader text={tableData.name}></BlockHeader>

      <UploadGrid isAutoHeight={isEditing}>
        {isEditing ? (
          <>
            <TableViewController
              tableHelper={tableHelper}
            ></TableViewController>

            <SaveTableController
              tableHelper={tableHelper}
              tableModel={tableModel}
              handleCancel={() => setIsEditing(false)}
            ></SaveTableController>
          </>
        ) : (
          <>
            <StaticTable>
              {header}

              {map(tableModel.links, (link) => {
                return (
                  <tr key={link.id}>
                    <td>
                      <StaticTableCellItem
                        text={link.data?.name || ''}
                      ></StaticTableCellItem>
                    </td>
                    <td>
                      <StaticTableCellItem
                        text={link.data?.description || ''}
                      ></StaticTableCellItem>
                    </td>
                    <td>
                      <StaticTableCellItemLink
                        text={link.data?.url || ''}
                      ></StaticTableCellItemLink>
                    </td>
                  </tr>
                );
              })}
            </StaticTable>

            {isLessLaptop ? (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: sp.s60 }}
              >
                <ButtonsContainer variant={isLessTablet ? 'vertical' : 'full'}>
                  <SecondaryButton onClick={toggleFavorite}>
                    {isFavorite
                      ? 'Убрать из избранного'
                      : 'Добавить в избранное'}
                  </SecondaryButton>
                  <PrimaryButton
                    onClick={() =>
                      tablesApi.download(
                        tableModel.id,
                        tableData.name || 'noname'
                      )
                    }
                  >
                    Скачать
                  </PrimaryButton>
                </ButtonsContainer>

                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography>Редактор: {tableData.username}</Typography>
                  <Typography>
                    Обновлено:{' '}
                    {formatBackToFrontDate(tableData.last_change_date)}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: sp.s60 }}
              >
                <FavoritesButton
                  isFavorite={Boolean(tableModel.data?.is_favorite)}
                  onToggle={toggleFavorite}
                ></FavoritesButton>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography>Редактор: {tableData.username}</Typography>
                  <Typography>
                    Обновлено:{' '}
                    {formatBackToFrontDate(tableData.last_change_date)}
                  </Typography>
                </Box>

                <ButtonsContainer variant="full">
                  {user?.isOnlyView ? null : (
                    <SecondaryButton onClick={() => setIsEditing(true)}>
                      Редактировать
                    </SecondaryButton>
                  )}
                  <PrimaryButton
                    onClick={() =>
                      tablesApi.download(
                        tableModel.id,
                        tableData.name || 'noname'
                      )
                    }
                  >
                    Скачать
                  </PrimaryButton>
                </ButtonsContainer>
              </Box>
            )}
          </>
        )}
      </UploadGrid>
    </PageInnerContainer>
  ) : (
    <></>
  );
});
